import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { useNewApiCall } from 'hooks';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { useForm } from 'react-hook-form';

import { EmployeesService } from '../components/services/EmployeesService';
import { Employee, RoleUI } from '../types/interfaces';
import { AddEmployeeProps } from '../types/props';

import '../ui/css/AddEmployeeCard.css';

import { CustomDropdown } from './custom-controls';
import ConfirmationDialog from './shared/ConfirmationDialog';
import { TagsComponent } from './TagsComponent';

interface RefInterface {
  handleCancel: () => void;
}

const AddEmployeeCard = React.forwardRef<RefInterface | null, AddEmployeeProps>(
  (
    {
      addedEmployees,
      onAdd,
      onDirtyChange,
      updateTree,
      closeDialog,
      withoutButtons,
      parentKey
    }: AddEmployeeProps,
    ref
  ) => {
    const {
      watch,
      setValue,
      formState: { isDirty }
    } = useForm<{ addedEmployees: Employee[]; employeeToAdd: Employee[] }>({
      defaultValues: { addedEmployees: addedEmployees, employeeToAdd: [] }
    });
    const [showDialog, setShowDialog] = useState(false);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const employeeToAdd = watch('employeeToAdd');
    const toast = useRef<Toast>(null);
    const filteredItems = useMemo(() => {
      return addedEmployees?.map((item) => item.key);
    }, [addedEmployees]);

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const executorNodes = employeeToAdd.map((executor) => ({
      key: executor.id.toString(), // Ensure the key is a string if TreeNode expects it
      label: executor.fullName // Assume TreeNode expects a label
    }));

    const handleDeleteExecutor = (taskId: string | number) => {
      const newEmployees = employeeToAdd.filter(
        (employee) => employee.id.toString() !== taskId.toString()
      );
      setValue('employeeToAdd', newEmployees, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (!isDirty && closeDialog) {
        closeDialog();
      }
    };

    const getEmployees = (data: Employee[]) => {
      return [...(data || [])].map((d) => {
        return d;
      });
    };

    const getEmployeesForDepartments = useCallback(async () => {
      if (filteredItems && filteredItems.length > 0) {
        EmployeesService.getForDepartments(Number(parentKey)).then((result) => {
          const data = result.data.value?.flatMap((x) =>
            x.id && x.fullName
              ? {
                  ...x,
                  key: x.id.toString(),
                  id: x.id,
                  label: x.fullName.toString()
                }
              : []
          );
          if (data) {
            const filtredData = data.filter(
              (item) =>
                !filteredItems?.includes(`${item.id}`) ||
                !filteredItems?.includes(
                  `${item.id.toString()?.split('department')?.[0]}`
                )
            );
            setEmployees(getEmployees(filtredData));
          }
        });
      }
      if (!filteredItems) {
        // TODO проверить, нужность этого места, т.к. тут идет запрос на моки
        console.log('!filteredItems');

        EmployeesService.getEmployeesXLarge().then((data: Employee[]) => {
          setEmployees(getEmployees(data));
        });
      }
    }, [filteredItems]);

    useNewApiCall(getEmployeesForDepartments, [filteredItems]);

    useImperativeHandle(ref, () => ({
      handleCancel
    }));

    const handleDataTableDropdownSelectionChange = (e: Employee[]) => {
      if (Array.isArray(e)) {
        setValue('employeeToAdd', e, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    };

    const onSubmit = useCallback(
      async (submittedData: Employee[]) => {
        submittedData.forEach((dataItem) => {
          if (onAdd && !dataItem.id) {
            dataItem.id = `${Date.now()}`;
            onAdd(dataItem);
          }
        });
        const employeeArray = submittedData.reduce(
          (acc: Employee[], item: RoleUI | Employee) => {
            if ('displayName' in item) {
              // item is a Role, do something with it if needed
            } else {
              // item is an Employee
              acc.push(item);
            }
            return acc;
          },
          []
        );
        if (parentKey !== undefined) {
          await EmployeesService.addToDepartment(
            submittedData.map((x) =>
              typeof x.id === 'string' ? parseInt(x.id) : x.id
            ),
            parseInt(parentKey)
          );
        }
        if (updateTree) updateTree(employeeArray);
        if (closeDialog) closeDialog();
      },
      [closeDialog, onAdd, parentKey, updateTree]
    );

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    return (
      <div>
        <Toast ref={toast} />
        <div className="dialog-tabless-card flex flex-column">
          <Divider />
          <div className="flex flex-column gap-3 pb-0">
            <div className="flex flex-column">
              <div className="flex flex-column">
                <div className="flex flex-column">
                  <div className="flex justify-content-between align-items-center gap-3">
                    <label className="col p-0" htmlFor="userRole">
                      Выберите сотрудников:
                    </label>
                    <div className="col-8 p-0">
                      <CustomDropdown<Employee>
                        multiplySelectMode
                        assetOptions={employees}
                        selectedAsset={employeeToAdd}
                        onSelectionChange={(e) =>
                          handleDataTableDropdownSelectionChange(
                            e as Employee[]
                          )
                        }
                        placeholder="Выбор сотрудников"
                        columns={[
                          {
                            field: 'personnelNumber',
                            header: 'Табельный номер',
                            width: '50%'
                          },
                          { field: 'fullName', header: 'ФИО', width: '50%' }
                        ]}
                        emptyMessage="Сотрудники не найдены."
                        overlayWidthMultiplier={1.5}
                      />
                    </div>
                  </div>
                  <p className="card-errors text-transparent"></p>
                </div>
              </div>
            </div>
          </div>

          <div
            className="flex flex-column"
            style={{
              marginBottom: '16px',
              maxHeight: '75px',
              minHeight: '75px'
            }}
          >
            <div className="flex flex-column">
              <div className="flex justify-content-between align-items-center">
                {employeeToAdd.length > 0 && (
                  <div
                    style={{
                      width: '100%',
                      maxHeight: '75px',
                      overflow: 'auto'
                    }}
                    id="tags-scroll"
                  >
                    <TagsComponent
                      passedClassName="gap-2"
                      initialTagsItems={executorNodes}
                      onDelete={handleDeleteExecutor}
                    />
                  </div>
                )}
                {employeeToAdd.length < 1 && (
                  <div
                    style={{
                      width: '100%',
                      maxHeight: '75px',
                      overflow: 'auto'
                    }}
                    id="tags-scroll"
                  >
                    Сотрудники не выбраны
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="default-card__buttons">
            <div className="flex gap-3">
              <Button
                label="Добавить"
                icon="pi pi-check"
                type="submit"
                onClick={() => {
                  const employeeArray: Employee[] = employeeToAdd.reduce(
                    (acc: Employee[], item: RoleUI | Employee) => {
                      if ('displayName' in item) {
                        // item is a Role, do something with it if needed
                      } else {
                        // item is an Employee
                        acc.push(item);
                      }
                      return acc;
                    },
                    []
                  );
                  onSubmit(employeeArray);
                }}
              />
              {!withoutButtons && (
                <>
                  <Button
                    label="Отмена"
                    icon="pi pi-times"
                    onClick={handleCancel}
                    className="p-button-secondary"
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />
      </div>
    );
  }
);

AddEmployeeCard.displayName = 'EmployeeCard';

export default AddEmployeeCard;
